import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Modal,
  Table,
  Tag,
  Input,
  Space,
  message,
} from "antd";
import {
  MailOutlined,
  InboxOutlined,
  ClockCircleOutlined,
  FileProtectOutlined,
  CheckOutlined,
  LockOutlined,
  BankOutlined,
  ReloadOutlined,
  SearchOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DateCustom from "../../../../hook/DateCustom";
import CurrencyFormat from "../../../../hook/CurrencyFormat";
import {
  enrichCase,
  exportCasesExcel,
  getRemainDebt,
  getDebt,
  loadLoanByContno,
  formatArea,
  getLocation,
  getMortgageText,
  isMortgaged,
  isSequestrated,
  sortCases,
  propertyDetailLabel,
  propertyTypeLabel,
} from "../EnforceCaseUtils";

const tagConfig = {
  all: {
    color: "#3357FF",
    textColor: "#fff",
    icon: <InboxOutlined />,
    label: "ทั้งหมด",
  },
  notSeize: {
    color: "#8c8c8c",
    textColor: "#fff",
    icon: <ClockCircleOutlined />,
    label: "ยังไม่ยึด",
  },
  setupSeize: {
    color: "#fa8c16",
    textColor: "#fff",
    icon: <FileProtectOutlined />,
    label: "ตั้งเรื่องยึด",
  },
  seizeSuccess: {
    color: "#52c41a",
    textColor: "#fff",
    icon: <CheckOutlined />,
    label: "ยึดสำเร็จ",
  },
  sequestrated: {
    color: "#cf1322",
    textColor: "#fff",
    icon: <LockOutlined />,
    label: "ติดอายัด",
  },
  mortgaged: {
    color: "#722ed1",
    textColor: "#fff",
    icon: <BankOutlined />,
    label: "ติดจำนอง",
  },
};

// สีของขั้นตอนการทำงาน ตามชื่อ (seize_status_text)
const seizeColorByLabel = Object.values(tagConfig).reduce(
  (map, c) => ({ ...map, [c.label]: c.color }),
  {},
);

const DataCheck = ({ open, close, data, status, lawyerName, subtitle }) => {
  const [, convertDateThaiShort] = DateCustom();
  const [, currencyFormatComma] = CurrencyFormat();
  const [loanByContno, setLoanByContno] = useState({});
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [exporting, setExporting] = useState(false);

  // รายการคดีของสถานะที่เลือก
  const dataArr = useMemo(
    () => (data && status ? data[status] || [] : []),
    [data, status],
  );

  useEffect(() => {
    if (open) {
      setKeyword("");
      loadLoanData();
    }
  }, [open, dataArr]);

  // โหลดข้อมูลสัญญาจาก POST_LOAN_DB2 ตามเลขสัญญา
  const loadLoanData = async () => {
    setLoading(true);
    try {
      setLoanByContno(await loadLoanByContno(dataArr));
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response ? error.response.data : error.message,
      );
      setLoanByContno({});
      message.error("ไม่พบข้อมูลสัญญา");
    } finally {
      setLoading(false);
    }
  };

  const tableData = useMemo(() => {
    const search = keyword.trim().toLowerCase();
    return sortCases(dataArr)
      .map((item) => enrichCase(item, loanByContno))
      .filter((item) => {
        if (!search) return true;
        const name = `${item.NAME1 || ""} ${item.NAME2 || ""}`;
        return (
          item.CONTNO?.toLowerCase().includes(search) ||
          item.LAWYER_NNAME?.toLowerCase().includes(search) ||
          name.toLowerCase().includes(search) ||
          item.possessor?.toLowerCase().includes(search) ||
          String(item.deed_number || "").toLowerCase().includes(search) ||
          item.red_case_number?.toLowerCase().includes(search)
        );
      });
  }, [dataArr, loanByContno, keyword]);

  const money = (value) =>
    value !== null && value !== undefined && value !== ""
      ? currencyFormatComma(value)
      : "-";

  const date = (value) => (value ? convertDateThaiShort(value) : "-");

  const wrapText = (text) => (
    <div
      style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        lineHeight: 1.5,
      }}
    >
      {text || "-"}
    </div>
  );

  const columns = [
    {
      title: "#",
      key: "index",
      width: 50,
      align: "center",
      fixed: "left",
      render: (_, __, index) => index + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "CONTNO",
      key: "CONTNO",
      width: 120,
      fixed: "left",
      render: (text) => (
        <span style={{ fontWeight: 600, color: "#1677ff" }}>{text || "-"}</span>
      ),
    },
    {
      title: "ชื่อลูกค้า",
      key: "customer",
      width: 200,
      render: (_, record) =>
        `${record.SNAM || ""}${record.NAME1 || ""} ${record.NAME2 || ""}`.trim() ||
        "-",
    },
    {
      title: "ผู้รับผิดชอบ",
      dataIndex: "LAWYER_NNAME",
      key: "LAWYER_NNAME",
      width: 120,
      render: (value) => value || "-",
    },
    {
      title: "สาขา",
      dataIndex: "LOCAT",
      key: "LOCAT",
      width: 80,
      align: "center",
      render: (value) => value || "-",
    },
    {
      title: "ขั้นตอนการทำงาน",
      dataIndex: "seize_status_text",
      key: "seize_status_text",
      width: 130,
      align: "center",
      render: (text) =>
        text ? <Tag color={seizeColorByLabel[text]}>{text}</Tag> : "-",
    },
    {
      title: "คดี",
      key: "case",
      children: [
        {
          title: "คดีแดง",
          dataIndex: "red_case_number",
          key: "red_case_number",
          width: 130,
          render: (text) => (
            <span style={{ color: "#cf1322" }}>{text || "-"}</span>
          ),
        },
        {
          title: "วันพิพากษา",
          dataIndex: "judge_date",
          key: "judge_date",
          width: 110,
          align: "center",
          render: (value) => date(value),
        },
      ],
    },
    {
      title: "สืบทรัพย์",
      key: "investigate",
      children: [
        {
          title: "วันที่สืบ",
          dataIndex: "investigation_date",
          key: "investigation_date",
          width: 110,
          align: "center",
          render: (value) => date(value),
        },
        {
          title: "ผู้ถือครอง",
          dataIndex: "possessor",
          key: "possessor",
          width: 180,
          render: (text) => wrapText(text?.trim()),
        },
        {
          title: "ประเภทเอกสารสิทธิ์",
          dataIndex: "property_type_id",
          key: "property_type_id",
          width: 120,
          align: "center",
          render: (id) => propertyTypeLabel(id),
        },
        {
          title: "ลักษณะทรัพย์",
          dataIndex: "property_detail_id",
          key: "property_detail_id",
          width: 170,
          render: (id) => propertyDetailLabel(id),
        },
        {
          title: "เลขโฉนด",
          dataIndex: "deed_number",
          key: "deed_number",
          width: 100,
          align: "center",
          render: (text) => text || "-",
        },
        {
          title: "เนื้อที่ (ไร่-งาน-วา)",
          key: "area",
          width: 130,
          align: "center",
          render: (_, record) => formatArea(record),
        },
        {
          title: "ที่ตั้ง",
          key: "location",
          width: 180,
          render: (_, record) =>
            wrapText(getLocation(record)),
        },
        {
          title: "ติดจำนอง",
          key: "mortgaged",
          width: 110,
          align: "center",
          render: (_, record) =>
            isMortgaged(record) ? (
              <Tag color="purple">ติดจำนอง</Tag>
            ) : (
              <Tag color="green">ไม่ติดจำนอง</Tag>
            ),
        },
        {
          title: "ภาระจำนอง",
          key: "mortgage",
          width: 240,
          render: (_, record) =>
            record.mortgage_type || record.mortgagee ? (
              <>
                {wrapText(getMortgageText(record))}
                {(record.mortgage_start_date || record.mortgage_end_date) && (
                  <div style={{ color: "#8c8c8c", lineHeight: 1.5 }}>
                    {date(record.mortgage_start_date)} -{" "}
                    {date(record.mortgage_end_date)}
                  </div>
                )}
              </>
            ) : (
              "-"
            ),
        },
        {
          title: "ยอดจำนอง",
          dataIndex: "mortgage_balance",
          key: "mortgage_balance",
          width: 120,
          align: "right",
          render: (value) => money(value),
        },
      ],
    },
    {
      title: "ยึดทรัพย์",
      key: "seize",
      children: [
        {
          title: "ติดอายัด",
          key: "sequestrate_status",
          width: 180,
          render: (_, record) =>
            isSequestrated(record) ? (
              <>
                <Tag color="red">ติดอายัด</Tag>
                {record.preference_creditor &&
                  wrapText(`กับ ${record.preference_creditor}`)}
              </>
            ) : (
              <Tag color="green">ไม่ติดอายัด</Tag>
            ),
        },
        {
          title: "วันที่ยึด",
          dataIndex: "seize_date",
          key: "seize_date",
          width: 110,
          align: "center",
          render: (value) => date(value),
        },
        {
          title: "สำนักงานบังคับคดี",
          dataIndex: "legal_execution_office",
          key: "legal_execution_office",
          width: 150,
          render: (text) => text || "-",
        },
        {
          title: "ราคาประเมิน (บังคับคดี)",
          dataIndex: "estimated_enforce_price",
          key: "estimated_enforce_price",
          width: 150,
          align: "right",
          render: (value) => money(value),
        },
      ],
    },
    {
      title: "ข้อมูลสัญญา",
      key: "loan",
      children: [
        {
          title: "ยอดหนี้",
          key: "NCSHPRC",
          width: 110,
          align: "right",
          render: (_, record) =>
            record.db2 ? money(getDebt(record.db2)) : "-",
        },
        {
          title: "จ่ายมาทั้งหมด",
          key: "SMPAY",
          width: 110,
          align: "right",
          render: (_, record) =>
            record.db2 ? currencyFormatComma(record.db2.LOAN?.SMPAY) : "-",
        },
        {
          title: "ยอดหนี้คงเหลือ",
          key: "remainDebt",
          width: 120,
          align: "right",
          render: (_, record) => {
            const remain = getRemainDebt(record.db2);
            if (remain === null) return "-";
            return (
              <span
                style={{
                  fontWeight: 600,
                  color: remain > 0 ? "#cf1322" : "#389e0d",
                }}
              >
                {remain < 0 ? "-" : ""}
                {currencyFormatComma(Math.abs(remain))}
              </span>
            );
          },
        },
        {
          title: "จ่ายล่าสุด",
          key: "LPAYD",
          width: 110,
          align: "center",
          render: (_, record) => date(record.db2?.LOAN?.LPAYD),
        },
      ],
    },
    {
      title: "หมายเหตุ",
      key: "mark",
      width: 240,
      render: (_, record) =>
        wrapText(
          [record.mark, record.seize_status_mark].filter(Boolean).join("\n"),
        ),
    },
  ];

  const config = tagConfig[status] || tagConfig.all;

  // ยอดรวมท้ายตาราง (ตามข้อมูลที่แสดงอยู่)
  const sumBy = (fn) =>
    Math.round(
      tableData.reduce((sum, r) => {
        const n = Number(fn(r));
        return sum + (isNaN(n) ? 0 : n);
      }, 0) * 100,
    ) / 100;

  const totals = {
    mortgage_balance: currencyFormatComma(
      sumBy((r) => r.mortgage_balance || 0),
    ),
    estimated_enforce_price: currencyFormatComma(
      sumBy((r) => r.estimated_enforce_price || 0),
    ),
    NCSHPRC: currencyFormatComma(sumBy((r) => getDebt(r.db2) || 0)),
    SMPAY: currencyFormatComma(sumBy((r) => r.db2?.LOAN?.SMPAY || 0)),
    remainDebt: currencyFormatComma(sumBy((r) => getRemainDebt(r.db2) || 0)),
  };

  const leafColumns = columns.flatMap((col) => col.children || [col]);

  const renderSummary = () =>
    tableData.length > 0 && (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ background: "#fff7e6", fontWeight: 700 }}>
          {leafColumns.map((col, index) =>
            index === 0 ? (
              <Table.Summary.Cell key="label" index={0} colSpan={3}>
                รวม {tableData.length} รายการ
              </Table.Summary.Cell>
            ) : index < 3 ? null : (
              <Table.Summary.Cell
                key={col.key}
                index={index}
                align={col.align === "right" ? "right" : undefined}
              >
                {totals[col.key] ?? ""}
              </Table.Summary.Cell>
            ),
          )}
        </Table.Summary.Row>
      </Table.Summary>
    );

  // export ตามตาราง (ข้อมูลที่กรองค้นหาอยู่)
  const onExport = async () => {
    setExporting(true);
    try {
      const name = config.label;
      await exportCasesExcel({
        sheets: [
          { name, color: `FF${config.color.slice(1)}`, rows: tableData },
        ],
        subtitle,
        formatDate: convertDateThaiShort,
        fileName: `รายงานยึดทรัพย์_${lawyerName || ""}_${name}_${dayjs().format(
          "YYYYMMDD_HHmm",
        )}.xlsx`.replace(/[\\/]/g, "-"),
      });
    } catch (error) {
      console.error(error);
      message.error("Export Excel ไม่สำเร็จ");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MailOutlined style={{ color: "#1890ff" }} />
          รายละเอียดข้อมูล
        </div>
      }
      open={open}
      width={1200}
      onCancel={() => close(false)}
      destroyOnClose
      footer={[
        <Button key="close" danger onClick={() => close(false)}>
          ปิด
        </Button>,
      ]}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <Tag
          color={config.color}
          style={{
            fontSize: 16,
            padding: "8px 14px",
            borderRadius: 10,
            fontWeight: "bold",
            color: config.textColor,
          }}
        >
          {config.icon} {config.label}: {dataArr.length} รายการ
        </Tag>
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="ค้นหาเลขสัญญา / ชื่อ / โฉนด / เลขคดี"
            style={{ width: 260 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <Button
            icon={<ReloadOutlined spin={loading} />}
            disabled={loading}
            onClick={loadLoanData}
          >
            รีโหลด
          </Button>
          <Button
            type="primary"
            style={{ background: "#217346" }}
            icon={<FileExcelOutlined />}
            loading={exporting}
            disabled={loading || tableData.length === 0}
            onClick={onExport}
          >
            Export Excel
          </Button>
        </Space>
      </div>

      <Table
        size="small"
        bordered
        rowKey={(record) => record.id ?? record.CONTNO}
        loading={loading}
        columns={columns}
        dataSource={tableData}
        scroll={{ x: 3800 }}
        pagination={false}
        summary={renderSummary}
        locale={{
          emptyText: (
            <div style={{ padding: 40, color: "#bfbfbf" }}>
              <InboxOutlined style={{ fontSize: 48 }} />
              <div style={{ marginTop: 8 }}>ไม่มีข้อมูล</div>
            </div>
          ),
        }}
      />

    </Modal>
  );
};

export default DataCheck;
